/**
 * Lightweight JWT helpers (no external deps). Client-side only.
 * Security note: decoding a JWT on the client does NOT verify its signature.
 * Use it only to read exp and show a UX hint; backend must still enforce auth.
 */

function base64UrlDecode(input: string): string {
  try {
    // Convert from base64url to base64
    const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
    // Pad if necessary
    const pad = base64.length % 4;
    const padded = pad ? base64 + "=".repeat(4 - pad) : base64;
    if (typeof window !== "undefined" && typeof window.atob === "function") {
      return decodeURIComponent(
        Array.prototype.map
          .call(window.atob(padded), (c: string) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
    }
    // Fallback for non-browser envs
    // eslint-disable-next-line no-undef
    return Buffer.from(padded, "base64").toString("utf-8");
  } catch {
    return "";
  }
}

export function decodeJwt(token: string): Record<string, any> | null {
  try {
    const parts = (token || "").split(".");
    if (parts.length !== 3) return null;
    const payload = base64UrlDecode(parts[1] || "");
    if (!payload) return null;
    const obj = JSON.parse(payload);
    return obj && typeof obj === "object" ? obj : null;
  } catch {
    return null;
  }
}

/**
 * Returns exp (in seconds since epoch) or null if not present/invalid.
 */
export function getJwtExp(token?: string | null): number | null {
  if (!token) return null;
  const payload = decodeJwt(token);
  const exp = payload && typeof payload.exp === "number" ? payload.exp : null;
  return Number.isFinite(exp as any) ? (exp as number) : null;
}

/**
 * Returns seconds left until expiration (0 if expired, null if unknown)
 */
export function getSecondsLeft(token?: string | null): number | null {
  const exp = getJwtExp(token);
  if (!exp) return null;
  const nowSec = Math.floor(Date.now() / 1000);
  return Math.max(0, exp - nowSec);
}

/**
 * Returns true if token is expired. Optional skew in seconds.
 */
export function isExpired(token?: string | null, skewSeconds = 0): boolean {
  const exp = getJwtExp(token);
  if (!exp) return false; // unknown
  const nowSec = Math.floor(Date.now() / 1000);
  return exp <= nowSec + Math.max(0, Math.floor(skewSeconds));
}

/**
 * Pretty format for seconds left (e.g. "2h 13m", "5m", "<1m", "0s")
 */
export function formatPrettyLeft(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds <= 0) return "0s";
  if (seconds < 60) return "<1m";
  const mins = Math.floor(seconds / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${mins % 60}m`;
  return `${mins}m`;
}